import { Suspense, lazy, ElementType } from 'react';
import { Navigate, useRoutes, useLocation } from 'react-router-dom';
// hooks
import useAuth from '../hooks/useAuth';
// layouts
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
// guards
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
import PermissionGuard from 'src/guards/PermissionGuard';
import PlanBasedGuard from '../guards/PlanBasedGuard';
import TenantOrganizationGuard from '../guards/TenantOrganizationGuard';
import BillingLockGuard from '../guards/BillingLockGuard';
// contexts
import { SupabaseRealtimeProvider } from '../contexts/SupabaseRealtimeContext';
// config
import { getDefaultLoginPath } from '../utils/loginPath';
import { resolvePlanTier } from '../utils/planGating';
// components
import LoadingScreen from '../components/LoadingScreen';
import ExternalRedirect from '../components/ExternalRedirect';
import { landingPath } from '../constants/landing';
import { PATH_DASHBOARD } from './paths';

// ----------------------------------------------------------------------

const Loadable = (Component: ElementType) => (props: any) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { isAuthenticated } = useAuth();

  const isDashboard = pathname.includes('/dashboard') && isAuthenticated;

  return (
    <Suspense fallback={<LoadingScreen isDashboard={isDashboard} />}>
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {

  const { user } = useAuth();

  const defaultLoginPath = getDefaultLoginPath(
    user?.permissions ?? [],
    resolvePlanTier(user?.plan_tier)
  )

  return useRoutes([
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
        {
          path: 'register',
          element: (
            <GuestGuard>
              <Register />
            </GuestGuard>
          ),
        },
        {
          path: 'sign-up',
          element: (
            <GuestGuard>
              <SignUp />
            </GuestGuard>
          ),
        },
        // { path: 'login-unprotected', element: <Login /> },
        // { path: 'register-unprotected', element: <Register /> },
        { path: 'invite/set-password', element: <InvitationNewPassword /> },
        { path: 'reset-password', element: <ResetPassword /> },
        { path: 'new-password', element: <NewPassword /> },
        { path: 'new-password/:resetToken', element: <NewPassword /> },
        { path: 'new-password/verify/:verifyToken', element: <VerifyNewPassword /> },
        { path: 'verify', element: <VerifyCode /> },
        { path: 'users/verify-email', element: <VerifyEmail /> },
        { path: 'venueusers/verify-email', element: <VerifyEmail /> },
      ],
    },

    // Dashboard Routes
    {
      path: 'dashboard',
      element: (
        <AuthGuard>
          <BillingLockGuard>
            <SupabaseRealtimeProvider>
              <DashboardLayout />
            </SupabaseRealtimeProvider>
          </BillingLockGuard>
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to={defaultLoginPath} replace />, index: true },
        { path: 'app',
          element: 
            <PermissionGuard hasContent permissions={['dashboard.read']}>
              <GeneralApp /> 
            </PermissionGuard>
        },
        // { path: 'ecommerce', element: <GeneralEcommerce /> },
        { path: 'analytics', element:
            <PermissionGuard hasContent permissions={['analytics.read']}>
              <PlanBasedGuard feature="analytics">
                <GeneralAnalytics />
              </PlanBasedGuard>
            </PermissionGuard>
        },
        // { path: 'banking', element: <GeneralBanking /> },
        // { path: 'booking', element: <GeneralBooking /> },
        {
          path: 'menu',
          children: [
            { element: <Navigate to="/dashboard/menu/list" replace />, index: true },
            // { path: 'shop', 
            //   element:
            //     <TenantOrganizationGuard>
            //       <PermissionGuard hasContent permissions={['menu.read']}>
            //         <EcommerceShop />
            //       </PermissionGuard>
            //     </TenantOrganizationGuard>
            // },
            { path: 'product/:name', 
              element:
                <TenantOrganizationGuard>
                  <PermissionGuard hasContent permissions={['menu.read']}>
                    <EcommerceProductDetails />
                  </PermissionGuard>
                </TenantOrganizationGuard>
            },
            { path: 'list', 
              element:
                <TenantOrganizationGuard>
                  <PermissionGuard hasContent permissions={['menu.read']}>
                    <MenuList />
                  </PermissionGuard>
                </TenantOrganizationGuard>
            },
            { path: 'product/new', 
              element:
                <TenantOrganizationGuard>
                  <PermissionGuard hasContent permissions={['menu.write']}>
                    <EcommerceProductCreate />
                  </PermissionGuard>
                </TenantOrganizationGuard>
            },
            { path: 'product/:id/edit', 
              element:
                <TenantOrganizationGuard>
                  <PermissionGuard hasContent permissions={['menu.write']}>
                    <EcommerceProductEdit />
                  </PermissionGuard>
                </TenantOrganizationGuard>
            },
            // { path: 'checkout', 
            //   element:
            //     <TenantOrganizationGuard>
            //       <PermissionGuard hasContent permissions={['menu.read']}>
            //         <EcommerceCheckout />
            //       </PermissionGuard>
            //     </TenantOrganizationGuard>
            // },
            { path: 'products', 
              element:
                <TenantOrganizationGuard>
                  <PermissionGuard hasContent permissions={['menu.read']}>
                    <EcommerceProductList />
                  </PermissionGuard>
                </TenantOrganizationGuard>
            },
            { path: 'categories', 
              element:
                <TenantOrganizationGuard>
                  <PermissionGuard hasContent permissions={['menu.read']}>
                    <MenuCategorytList />
                  </PermissionGuard>
                </TenantOrganizationGuard>
            },
            { path: 'category/new', 
              element:
                <TenantOrganizationGuard>
                  <PermissionGuard hasContent permissions={['menu.write']}>
                    <MenuCategorytCreate />
                  </PermissionGuard>
                </TenantOrganizationGuard>
            },
            { path: 'category/:name/edit', 
              element:
                <TenantOrganizationGuard>
                  <PermissionGuard hasContent permissions={['menu.write']}>
                    <MenuCategorytCreate />
                  </PermissionGuard>
                </TenantOrganizationGuard>
            },
          ],
        },
        {
          path: 'venue',
          children: [
            { element: <Navigate to="/dashboard/venue/cards" replace />, index: true },
            // { element: <Venues />, index: true },
            { path: 'profile', 
              element:
                <TenantOrganizationGuard>
                  <PermissionGuard hasContent permissions={['venues.read']}>
                    <VenueProfile />
                  </PermissionGuard>
                </TenantOrganizationGuard>
            },
            { path: 'cards', 
              element:
                <TenantOrganizationGuard>
                  <PermissionGuard hasContent permissions={['venues.read']}>
                    <VenueCards />
                  </PermissionGuard>
                </TenantOrganizationGuard>
            },
            { path: 'list', 
              element:
                <TenantOrganizationGuard>
                  <PermissionGuard hasContent permissions={['venues.read']}>
                    <VenueList />
                  </PermissionGuard>
                </TenantOrganizationGuard>
            },
            { path: 'schedule-table', 
              element:
                <TenantOrganizationGuard>
                  <PermissionGuard hasContent permissions={['venues.read']}>
                    <VenueTable />
                  </PermissionGuard>
                </TenantOrganizationGuard>
            },
            { path: 'new', 
              element:
                <TenantOrganizationGuard>
                  <PermissionGuard hasContent permissions={['venues.write']}>
                    <VenueCreate />
                  </PermissionGuard>
                </TenantOrganizationGuard>
            },
            { path: ':name/edit', 
              element:
                <TenantOrganizationGuard>
                  <PermissionGuard hasContent permissions={['venues.read']}>
                    <VenueProfile />
                  </PermissionGuard>
                </TenantOrganizationGuard>
            },
            { path: ':id/inventory/entry', 
            element:
              <TenantOrganizationGuard>
                <PermissionGuard hasContent permissions={['inventory.write']}>
                  <InventoryCreate />
                </PermissionGuard>
              </TenantOrganizationGuard>
          },
            { path: ':id/payouts',
              element:
                <TenantOrganizationGuard>
                  <PermissionGuard hasContent permissions={['venues.connect']}>
                    <VenuePayouts />
                  </PermissionGuard>
                </TenantOrganizationGuard>
            },
            // { path: 'account', element: <UserAccount /> },
          ],
        },
        {
          path: 'organization',
          children: [
            {
              path: 'subscription',
              element: (
                <TenantOrganizationGuard>
                  <PermissionGuard hasContent permissions={['billing.manage']}>
                    <OrganizationSubscription />
                  </PermissionGuard>
                </TenantOrganizationGuard>
              ),
            },
          ],
        },
        {
          path: 'user',
          children: [
            { element: <Navigate to="/dashboard/user/list" replace />, index: true },
            { path: 'profile',
              element:
                <PermissionGuard hasContent permissions={['users.read']}>
                  <UserProfile />
                </PermissionGuard>
            },
            { path: 'cards',
              element:
                <PermissionGuard hasContent permissions={['users.read']}>
                  <UserCards />
                </PermissionGuard>
            },
            { path: 'list',
              element:
                <PermissionGuard hasContent permissions={['users.read']}>
                  <UserList />
                </PermissionGuard>
            },
            { path: 'new',
              element:
                <PermissionGuard hasContent permissions={['users.write']}>
                  <UserCreate />
                </PermissionGuard>
            },
            { path: ':id/edit',
              element:
                <PermissionGuard hasContent permissions={['users.read']}>
                  <UserProfile />
                </PermissionGuard>
            },
            { path: 'account',
              element:
                <UserAccount />
            },
          ],
        },
        {
          path: 'order',
          children: [
            { element: <Navigate to="/dashboard/order/kanban" replace />, index: true },
            { path: 'kanban', element: <PermissionGuard hasContent permissions={['orders.read']}><OrderKanban /></PermissionGuard> },
            { path: 'list', element: <PermissionGuard hasContent permissions={['orders.read']}><OrderList /></PermissionGuard> },
            { path: ':id', element: <PermissionGuard hasContent permissions={['orders.read']}><OrderDetails /></PermissionGuard> },
            // { path: ':id/edit', element: <OrderEdit /> },
            // { path: 'new', element: <OrderCreate /> },
          ],
        },
        {
          path: 'tab',
          children: [
            { element: <Navigate to="/dashboard/tab/kanban" replace />, index: true },
            { path: 'kanban', element: <PermissionGuard hasContent permissions={['tabs.read']}><PlanBasedGuard feature="tabs"><TabKanban /></PlanBasedGuard></PermissionGuard> },
            { path: 'list', element: <PermissionGuard hasContent permissions={['tabs.read']}><PlanBasedGuard feature="tabs"><TabList /></PlanBasedGuard></PermissionGuard> },
            { path: 'new', element: <PermissionGuard hasContent permissions={['tabs.write']}><PlanBasedGuard feature="tabs"><TabCreate /></PlanBasedGuard></PermissionGuard> },
            { path: ':id/order/new', element: <PermissionGuard hasContent permissions={['tabs.write']}><PlanBasedGuard feature="tabs"><TabOrderCreate /></PlanBasedGuard></PermissionGuard> },
            { path: ':id', element: <PermissionGuard hasContent permissions={['tabs.read']}><PlanBasedGuard feature="tabs"><OrderDetails /></PlanBasedGuard></PermissionGuard> },
            // { path: ':id/edit', element: <OrderEdit /> },
          ],
        },
        {
          path: 'inventory',
          children: [
            { element: <Navigate to="/dashboard/inventory/list" replace />, index: true },
            { path: 'control',
              element:
                <PermissionGuard hasContent permissions={['inventory.read']}>
                  <PlanBasedGuard feature="inventory">
                    <InventoryControl />
                  </PlanBasedGuard>
                </PermissionGuard>
            },
            { path: 'statement',
              element:
                <PermissionGuard hasContent permissions={['inventory.read']}>
                  <PlanBasedGuard feature="inventory">
                    <InventoryStatement />
                  </PlanBasedGuard>
                </PermissionGuard>
            },
            { path: 'create',
              element:
                <PermissionGuard hasContent permissions={['inventory.write']}>
                  <PlanBasedGuard feature="inventory">
                    <InventoryCreate />
                  </PlanBasedGuard>
                </PermissionGuard>
            },
            // { path: ':id/create', 
            //   element:
            //     <PermissionGuard hasContent permissions={['inventory.read']}>
            //       <InventoryCreate />
            //     </PermissionGuard>
            // },
          ],
        },
        {
          path: 'invoice',
          children: [
            { element: <Navigate to="/dashboard/invoice/kanban" replace />, index: true },
            { path: 'kanban',
              element:
                <PermissionGuard hasContent permissions={['invoices.read']}>
                  <InvoiceKanban />
                </PermissionGuard>
            },
            { path: 'list',
              element:
                <PermissionGuard hasContent permissions={['invoices.read']}>
                  <InvoiceList />
                </PermissionGuard>
            },
            { path: ':id',
              element:
                <PermissionGuard hasContent permissions={['invoices.read']}>
                  <InvoiceDetails />
                </PermissionGuard>
            },
            { path: ':id/edit',
              element:
                <PermissionGuard hasContent permissions={['invoices.read']}>
                  <InvoiceEdit />
                </PermissionGuard>
            },
            { path: 'new',
              element:
                <PermissionGuard hasContent permissions={['invoices.read']}>
                  <InvoiceCreate />
                </PermissionGuard>
            },
          ],
        },
        // {
        //   path: 'security',
        //   children: [
        //     { element: <Navigate to="/dashboard/security/list" replace />, index: true },
        //     { path: 'role/list',
        //       element:
        //         <PermissionGuard hasContent roles={['admin', 'manager']}>
        //           <RoleList />
        //         </PermissionGuard>
        //     },
        //     { path: 'user/list',
        //       element:
        //         <PermissionGuard hasContent roles={['admin', 'manager']}>
        //           <VenueList />
        //         </PermissionGuard>
        //     },
        //     { path: 'venue/list',
        //       element:
        //         <PermissionGuard hasContent roles={['admin', 'manager']}>
        //           <VenueList />
        //         </PermissionGuard>
        //     },
        //     { path: 'group/list',
        //       element:
        //         <PermissionGuard hasContent roles={['admin', 'manager']}>
        //           <VenueList />
        //         </PermissionGuard>
        //     },
        //     { path: 'reports',
        //       element:
        //         <PermissionGuard hasContent roles={['admin', 'manager']}>
        //           <VenueList />
        //         </PermissionGuard>
        //     },
        //     { path: 'new',
        //       element:
        //         <PermissionGuard hasContent roles={['admin', 'manager']}>
        //           <VenueCreate />
        //         </PermissionGuard>
        //     },
        //     { path: ':name/edit',
        //       element:
        //         <PermissionGuard hasContent roles={['admin', 'manager']}>
        //           <VenueProfile />
        //         </PermissionGuard>
        //     },
        //   ],
        // },
        // {
        //   path: 'blog',
        //   children: [
        //     { element: <Navigate to="/dashboard/blog/posts" replace />, index: true },
        //     { path: 'posts', element: <BlogPosts /> },
        //     { path: 'post/:title', element: <BlogPost /> },
        //     { path: 'new', element: <BlogNewPost /> },
        //   ],
        // },
        // {
        //   path: 'mail',
        //   children: [
        //     { element: <Navigate to="/dashboard/mail/all" replace />, index: true },
        //     { path: 'label/:customLabel', element: <Mail /> },
        //     { path: 'label/:customLabel/:mailId', element: <Mail /> },
        //     { path: ':systemLabel', element: <Mail /> },
        //     { path: ':systemLabel/:mailId', element: <Mail /> },
        //   ],
        // },
        // {
        //   path: 'chat',
        //   children: [
        //     { element: <Chat />, index: true },
        //     { path: 'new', element: <Chat /> },
        //     { path: ':conversationKey', element: <Chat /> },
        //   ],
        // },
        // { path: 'calendar', element: <Calendar /> },
        // { path: 'kanban', element: <Kanban /> },
        {
          path: 'security',
          children: [
            { element: <Navigate to="/dashboard/security/profiles" replace />, index: true },
            {
              path: 'profiles',
              element: (
                <PermissionGuard hasContent permissions={['security.read']}>
                  <AccessProfilesList />
                </PermissionGuard>
              ),
            },
          ],
        },
        { path: 'permission-denied', element: <PermissionDenied /> },
      ],
    },

    // Fastpass short link — https://web.checktech.app/f/{token}
    {
      path: 'f/:token',
      element: (
        <GuestGuard>
          <FastpassTokenRedirect />
        </GuestGuard>
      ),
    },

    // Alternative Fastpass Routes
    {
      path: 'fastpass',
      children: [
        { element: <Navigate to="/fastpass/checkout" replace />, index: true },
        { path: 'checkout', 
          element:
            <GuestGuard>
              <FastpassCheckout replace />
            </GuestGuard>
        },
      ],
    },

    // Main Routes
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: 'coming-soon', element: <ComingSoon /> },
        { path: 'maintenance', element: <Maintenance /> },
        // { path: 'pricing', element: <Pricing /> },
        // { path: 'payment', element: <Payment /> },
        { path: '500', element: <Page500 /> },
        { path: '404', element: <Page404 /> },
        { path: '403', element: <Page403 /> },
        {
          path: 'billing-locked',
          element: (
            <AuthGuard>
              <BillingLocked />
            </AuthGuard>
          ),
        },
        { path: '*', element: <Navigate to="/404" replace /> },
      ],
    },
    // Landing page routes — served by mvp-check-landing (checktech.app)
    {
      path: '/',
      children: [
        { element: <Navigate to={PATH_DASHBOARD.root} replace />, index: true },
        { path: 'parceiros', element: <ExternalRedirect to={landingPath()} /> },
        { path: 'clientes', element: <ExternalRedirect to={landingPath('/clients')} /> },
        { path: 'clients', element: <ExternalRedirect to={landingPath('/clients')} /> },
        { path: 'sobre-nos', element: <ExternalRedirect to={landingPath('/about-us')} /> },
        { path: 'about-us', element: <ExternalRedirect to={landingPath('/about-us')} /> },
        { path: 'contato', element: <ExternalRedirect to={landingPath('/contact')} /> },
        { path: 'contact', element: <ExternalRedirect to={landingPath('/contact')} /> },
        { path: 'faqs', element: <ExternalRedirect to={landingPath('/faqs')} /> },
        { path: 'termos', element: <ExternalRedirect to={landingPath('/terms-conditions')} /> },
        { path: 'terms-conditions', element: <ExternalRedirect to={landingPath('/terms-conditions')} /> },
        { path: 'politica-de-privacidade', element: <ExternalRedirect to={landingPath('/privacy-policy')} /> },
        { path: 'privacy-policy', element: <ExternalRedirect to={landingPath('/privacy-policy')} /> },
        { path: 'contact-us', element: <ExternalRedirect to={landingPath('/contact')} /> },
      ],
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}

// AUTHENTICATION
const Login = Loadable(lazy(() => import('../pages/auth/Login')));
const Register = Loadable(lazy(() => import('../pages/auth/Register')));
const SignUp = Loadable(lazy(() => import('../pages/auth/SignUp')));
const InvitationNewPassword = Loadable(lazy(() => import('../pages/auth/InvitationAccept')));
const ResetPassword = Loadable(lazy(() => import('../pages/auth/ResetPassword')));
const NewPassword = Loadable(lazy(() => import('../pages/auth/NewPassword')));
const VerifyNewPassword = Loadable(lazy(() => import('../pages/auth/VerifyCodeNewPassword')));
const VerifyCode = Loadable(lazy(() => import('../pages/auth/VerifyCode')));
const VerifyEmail = Loadable(lazy(() => import('../pages/auth/VerifyEmail')));

// DASHBOARD

// GENERAL
const GeneralApp = Loadable(lazy(() => import('../pages/dashboard/GeneralApp')));
const GeneralEcommerce = Loadable(lazy(() => import('../pages/dashboard/GeneralEcommerce')));
const GeneralAnalytics = Loadable(lazy(() => import('../pages/dashboard/GeneralAnalytics')));
const GeneralBanking = Loadable(lazy(() => import('../pages/dashboard/GeneralBanking')));
const GeneralBooking = Loadable(lazy(() => import('../pages/dashboard/GeneralBooking')));

// ORDER
const OrderList = Loadable(lazy(() => import('../pages/dashboard/OrderList')));
const OrderKanban = Loadable(lazy(() => import('../pages/dashboard/OrderKanban')));
const OrderDetails = Loadable(lazy(() => import('../pages/dashboard/OrderDetails')));
// const OrderCreate = Loadable(lazy(() => import('../pages/dashboard/OrderCreate')));
// const OrderEdit = Loadable(lazy(() => import('../pages/dashboard/OrderEdit')));

// TAB
const TabKanban = Loadable(lazy(() => import('../pages/dashboard/TabKanban')));
const TabList = Loadable(lazy(() => import('../pages/dashboard/TabList')));
// const TabDetails = Loadable(lazy(() => import('../pages/dashboard/TabDetails')));
const TabCreate = Loadable(lazy(() => import('../pages/dashboard/TabCreate')));
const TabOrderCreate = Loadable(lazy(() => import('../pages/dashboard/TabOrderCreate')));
// const TabEdit = Loadable(lazy(() => import('../pages/dashboard/TabEdit')));

// FASTPASS
const FastpassCheckout = Loadable(lazy(() => import('../pages/fastpass/EcommerceCheckout')));
const FastpassTokenRedirect = Loadable(lazy(() => import('../pages/fastpass/FastpassTokenRedirect')));

// PRODUCTS
const EcommerceShop = Loadable(lazy(() => import('../pages/dashboard/EcommerceShop')));
const EcommerceProductDetails = Loadable(lazy(() => import('../pages/dashboard/EcommerceProductDetails')));
const EcommerceProductList = Loadable(lazy(() => import('../pages/dashboard/EcommerceProductList')));
const EcommerceProductCreate = Loadable(lazy(() => import('../pages/dashboard/EcommerceProductCreate')));
const EcommerceProductEdit = Loadable(lazy(() => import('../pages/dashboard/EcommerceProductEdit')));
const EcommerceCheckout = Loadable(lazy(() => import('../pages/dashboard/EcommerceCheckout')));
const MenuList = Loadable(lazy(() => import('../pages/dashboard/MenuList')))
const MenuCategorytList = Loadable(lazy(() => import('../pages/dashboard/MenuCategoryList')));
const MenuCategorytCreate = Loadable(lazy(() => import('../pages/dashboard/MenuCategoryCreate')));

// VENUES
const VenueProfile = Loadable(lazy(() => import('../pages/dashboard/VenueProfile')));
const VenueCards = Loadable(lazy(() => import('../pages/dashboard/VenueCards')));
const VenueList = Loadable(lazy(() => import('../pages/dashboard/VenueList')));
const VenueTable = Loadable(lazy(() => import('../pages/dashboard/VenueTable')));
const Venues = Loadable(lazy(() => import('../pages/dashboard/VenuesMain')));
// const VenueAccount = Loadable(lazy(() => import('../pages/dashboard/VenueAccount')));
const VenueCreate = Loadable(lazy(() => import('../pages/dashboard/VenueCreate')));
const VenuePayouts = Loadable(lazy(() => import('../pages/dashboard/VenuePayouts')));
const OrganizationSubscription = Loadable(
  lazy(() => import('../pages/dashboard/OrganizationSubscription'))
);

// INVENTORY
const InventoryCreate = Loadable(lazy(() => import('../pages/dashboard/InventoryCreate')));
const InventoryControl = Loadable(lazy(() => import('../pages/dashboard/InventoryControl')));
// const InventoryEdit = Loadable(lazy(() => import('../pages/dashboard/InventoryEdit')));
const InventoryStatement = Loadable(lazy(() => import('../pages/dashboard/InventoryStatement')));

// INVOICE
const InvoiceKanban = Loadable(lazy(() => import('../pages/dashboard/InvoiceKanban')));
const InvoiceList = Loadable(lazy(() => import('../pages/dashboard/InvoiceList')));
const InvoiceDetails = Loadable(lazy(() => import('../pages/dashboard/InvoiceDetails')));
const InvoiceCreate = Loadable(lazy(() => import('../pages/dashboard/InvoiceCreate')));
const InvoiceEdit = Loadable(lazy(() => import('../pages/dashboard/InvoiceEdit')));

// BLOG
const BlogPosts = Loadable(lazy(() => import('../pages/dashboard/BlogPosts')));
const BlogPost = Loadable(lazy(() => import('../pages/dashboard/BlogPost')));
const BlogNewPost = Loadable(lazy(() => import('../pages/dashboard/BlogNewPost')));

// USER
const CurrentUserProfile = Loadable(lazy(() => import('../pages/dashboard/CurrentUserProfile')));
const UserProfile = Loadable(lazy(() => import('../pages/dashboard/UserProfile')));
const UserCards = Loadable(lazy(() => import('../pages/dashboard/UserCards')));
const UserList = Loadable(lazy(() => import('../pages/dashboard/UserList')));
const UserAccount = Loadable(lazy(() => import('../pages/dashboard/UserAccount')));
const UserCreate = Loadable(lazy(() => import('../pages/dashboard/UserCreate')));

// APP
const Chat = Loadable(lazy(() => import('../pages/dashboard/Chat')));
const Mail = Loadable(lazy(() => import('../pages/dashboard/Mail')));
const Calendar = Loadable(lazy(() => import('../pages/dashboard/Calendar')));
const Kanban = Loadable(lazy(() => import('../pages/dashboard/Kanban')));

// SECURITY
const RoleList = Loadable(lazy(() => import('../pages/dashboard/RoleList')));
const AccessProfilesList = Loadable(lazy(() => import('../pages/dashboard/AccessProfilesList')))

// TEST RENDER PAGE BY ROLE
const PermissionDenied = Loadable(lazy(() => import('../pages/dashboard/PermissionDenied')));

// MAIN
const ComingSoon = Loadable(lazy(() => import('../pages/ComingSoon')));
const Maintenance = Loadable(lazy(() => import('../pages/Maintenance')));
const Pricing = Loadable(lazy(() => import('../pages/Pricing')));
const Payment = Loadable(lazy(() => import('../pages/Payment')));
const Page500 = Loadable(lazy(() => import('../pages/Page500')));
const Page403 = Loadable(lazy(() => import('../pages/Page403')));
const Page404 = Loadable(lazy(() => import('../pages/Page404')));
const BillingLocked = Loadable(lazy(() => import('../pages/BillingLocked')));
